import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../models/auth-api.model';
import { PageResult } from '../models/api.model';
import {
  ChatMessage,
  ChatMessageComment,
  CommunicationContact,
  CommunicationDirectoryGroup,
  ConversationSummary,
  CreateChatMessageCommentDto,
  SendChatMessageDto,
  StartConversationDto,
  UpdateChatMessageCommentDto,
} from '../models/communication-api.model';
import { SessionService } from './session.service';
import { UserRole } from '../models/user-role.enum';

const toParams = (query: Record<string, unknown>): HttpParams => {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params = params.set(key, String(value));
    }
  }
  return params;
};

/**
 * REST client for the communication bounded context.
 * Role segment is derived from the session (student | teacher).
 */
@Injectable({ providedIn: 'root' })
export class CommunicationApiService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);

  private roleRoot(): 'student' | 'teacher' {
    return this.session.role() === UserRole.Teacher ? 'teacher' : 'student';
  }

  private base(): string {
    return `${environment.apiUrl}/communication/${this.roleRoot()}`;
  }

  contacts(search?: string): Observable<CommunicationContact[]> {
    return this.http
      .get<ApiEnvelope<CommunicationContact[]>>(`${this.base()}/contacts`, {
        params: toParams({ search }),
      })
      .pipe(map((r) => r.data));
  }

  /** Students per course (teacher) or the course teacher (student). */
  directory(): Observable<CommunicationDirectoryGroup[]> {
    return this.http
      .get<ApiEnvelope<CommunicationDirectoryGroup[]>>(`${this.base()}/directory`)
      .pipe(map((r) => r.data));
  }

  conversations(pageNumber = 1, pageSize = 50): Observable<PageResult<ConversationSummary>> {
    return this.http
      .get<ApiEnvelope<PageResult<ConversationSummary>>>(`${this.base()}/conversations`, {
        params: toParams({ pageNumber, pageSize }),
      })
      .pipe(map((r) => r.data));
  }

  startConversation(dto: StartConversationDto): Observable<ConversationSummary> {
    return this.http
      .post<ApiEnvelope<ConversationSummary>>(`${this.base()}/conversations`, dto)
      .pipe(map((r) => r.data));
  }

  conversation(id: string): Observable<ConversationSummary> {
    return this.http
      .get<ApiEnvelope<ConversationSummary>>(`${this.base()}/conversations/${id}`)
      .pipe(map((r) => r.data));
  }

  messages(
    conversationId: string,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<PageResult<ChatMessage>> {
    return this.http
      .get<ApiEnvelope<PageResult<ChatMessage>>>(
        `${this.base()}/conversations/${conversationId}/messages`,
        { params: toParams({ pageNumber, pageSize }) },
      )
      .pipe(map((r) => r.data));
  }

  sendMessage(conversationId: string, dto: SendChatMessageDto): Observable<ChatMessage> {
    return this.http
      .post<ApiEnvelope<ChatMessage>>(
        `${this.base()}/conversations/${conversationId}/messages`,
        dto,
      )
      .pipe(map((r) => r.data));
  }

  markRead(conversationId: string): Observable<{ ok: true }> {
    return this.http
      .post<ApiEnvelope<{ ok: true }>>(
        `${this.base()}/conversations/${conversationId}/read`,
        {},
      )
      .pipe(map((r) => r.data));
  }

  createMessageComment(
    conversationId: string,
    messageId: string,
    dto: CreateChatMessageCommentDto,
  ): Observable<ChatMessageComment> {
    return this.http
      .post<ApiEnvelope<ChatMessageComment>>(
        `${this.base()}/conversations/${conversationId}/messages/${messageId}/comments`,
        dto,
      )
      .pipe(map((r) => r.data));
  }

  listConversationComments(conversationId: string): Observable<ChatMessageComment[]> {
    return this.http
      .get<ApiEnvelope<ChatMessageComment[]>>(
        `${this.base()}/conversations/${conversationId}/comments`,
      )
      .pipe(map((r) => r.data));
  }

  updateMessageComment(
    conversationId: string,
    commentId: string,
    dto: UpdateChatMessageCommentDto,
  ): Observable<ChatMessageComment> {
    return this.http
      .patch<ApiEnvelope<ChatMessageComment>>(
        `${this.base()}/conversations/${conversationId}/comments/${commentId}`,
        dto,
      )
      .pipe(map((r) => r.data));
  }
}
